import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import LobbyJoinedPlayerRow from './LobbyJoinedPlayerRow';
import { IMAGE_CONSTANTS } from '../constants/ImageConstans';
import { TEXT_LABELS } from '../constants/TextLabelConstants';
import { LIVE_GAME_SECTION_TITLE_STYLE } from '../constants/housieLiveStyles';
import './LiveGameMembersSidebar.css';

const padCount = (n) => {
  const count = Math.max(0, n || 0);
  if (count === 0) return '0';
  return String(count).padStart(2, '0');
};

/**
 * Figma 18423:6237 — Rap Housie live right sidebar
 * Top: CLAIMED winners (claim type + XP prize)
 * Bottom: TOTAL MEMBERS (all joined players)
 */
const LiveGameMembersSidebar = ({
  claimedWinners = [],
  members = [],
  defaultClaimedOpen = true,
  defaultMembersOpen = false,
}) => {
  const claimedCount = claimedWinners?.length || 0;
  const membersCount = members?.length || 0;
  const [claimedOpen, setClaimedOpen] = useState(defaultClaimedOpen);
  const [membersOpen, setMembersOpen] = useState(defaultMembersOpen);

  useEffect(() => {
    if (claimedCount > 0) {
      setClaimedOpen(true);
      setMembersOpen(false);
    }
  }, [claimedCount]);

  const claimedTitle =
    TEXT_LABELS.RAP_HOUSIE_LIVE_GAME_CALIMED_USERS_LIST_TITLE || 'Claimed';
  const membersTitle =
    TEXT_LABELS.RAP_HOUSIE_LIVE_GAME_TOTAL_MEMBERS_PREFIX || 'TOTAL MEMBERS';

  return (
    <div className="live-members-sidebar" data-node-id="18423:6237">
      <section
        className={`live-members-sidebar__card${claimedOpen ? ' is-expanded' : ' is-collapsed'}`}
        data-node-id="18423:5855"
        aria-label={`${claimedTitle} (${claimedCount})`}
      >
        <button
          type="button"
          className="live-members-sidebar__header"
          onClick={() => setClaimedOpen((open) => !open)}
          aria-expanded={claimedOpen}
        >
          <h2 style={LIVE_GAME_SECTION_TITLE_STYLE}>
            {`${claimedTitle.toUpperCase()} (${padCount(claimedCount)})`}
          </h2>
          <img
            className={`live-members-sidebar__chevron${claimedOpen ? ' is-up' : ''}`}
            src={IMAGE_CONSTANTS.V2_HOUSIE_COLLAPSE_CHEVRON_UP}
            alt=""
            width={20}
            height={20}
            aria-hidden="true"
          />
        </button>

        {claimedOpen ? (
          <div className="live-members-sidebar__list" data-node-id="18423:5861">
            {claimedCount > 0 ? (
              claimedWinners.map((winner, index) => {
                const key =
                  winner?.id ||
                  `${winner?.userId || 'claim'}-${winner?.claimType || index}`;
                const username =
                  winner?.username || winner?.userName || winner?.name || 'Player';
                const avatar =
                  winner?.avatar ||
                  winner?.profilePic ||
                  winner?.userImage ||
                  IMAGE_CONSTANTS.V2_IMAGE_PLACEHOLDER;
                const claimLabel = String(
                  winner?.displayClaimType || winner?.claimLabel || winner?.claimType || ''
                )
                  .replace(/_/g, ' ')
                  .trim()
                  .toUpperCase();
                const points = winner?.points ?? winner?.prize ?? 0;
                const showPrize = points > 0;

                return (
                  <div key={key} className="live-members-sidebar__claimed-row">
                    <div className="live-members-sidebar__claimed-left">
                      <img
                        className="live-members-sidebar__avatar"
                        src={avatar}
                        alt={username}
                      />
                      <div className="live-members-sidebar__claimed-copy">
                        <span className="live-members-sidebar__name">{username}</span>
                        {claimLabel ? (
                          <span className="live-members-sidebar__claim-type">
                            {claimLabel}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {showPrize ? (
                      <div className="live-members-sidebar__prize" aria-label={`${points} XP`}>
                        <img
                          className="live-members-sidebar__prize-coin"
                          src={IMAGE_CONSTANTS.V2_XP_COIN}
                          alt=""
                          width={24}
                          height={24}
                        />
                        <span className="live-members-sidebar__prize-value">{points}</span>
                      </div>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <p className="live-members-sidebar__empty">No claims yet</p>
            )}
          </div>
        ) : null}
      </section>

      <section
        className={`live-members-sidebar__card${membersOpen ? ' is-expanded' : ' is-collapsed'}`}
        data-node-id="18423:5924"
        aria-label={`${membersTitle} (${membersCount})`}
      >
        <button
          type="button"
          className="live-members-sidebar__header"
          onClick={() => setMembersOpen((open) => !open)}
          aria-expanded={membersOpen}
        >
          <h2 style={LIVE_GAME_SECTION_TITLE_STYLE}>
            {`${membersTitle} (${membersCount})`}
          </h2>
          <img
            className={`live-members-sidebar__chevron${membersOpen ? ' is-up' : ''}`}
            src={IMAGE_CONSTANTS.V2_HOUSIE_COLLAPSE_CHEVRON_UP}
            alt=""
            width={20}
            height={20}
            aria-hidden="true"
          />
        </button>

        {membersOpen ? (
          <div className="live-members-sidebar__list">
            {membersCount > 0 ? (
              members.map((player, index) => {
                const key = player?.id || player?.userId || `user-${index}`;
                const username = player?.username || player?.userName || '';
                const avatar =
                  player?.avatar ||
                  player?.profilePic ||
                  player?.userImage ||
                  IMAGE_CONSTANTS.V2_IMAGE_PLACEHOLDER;
                return (
                  <LobbyJoinedPlayerRow
                    key={key}
                    username={username}
                    avatar={avatar}
                  />
                );
              })
            ) : (
              <p className="live-members-sidebar__empty">
                {TEXT_LABELS.RAP_HOUSIE_ROOM_GAME_CODE_SHARE_NO_PLAYER_SUBTITLE ||
                  'Waiting for members to join'}
              </p>
            )}
          </div>
        ) : null}
      </section>
    </div>
  );
};

LiveGameMembersSidebar.propTypes = {
  claimedWinners: PropTypes.arrayOf(PropTypes.object),
  members: PropTypes.arrayOf(PropTypes.object),
  defaultClaimedOpen: PropTypes.bool,
  defaultMembersOpen: PropTypes.bool,
};

export default LiveGameMembersSidebar;
